import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { inbox, tickets } from '@repletoryreach/sdk-api';
import { useDebounce } from '@repletoryreach/sdk-utils/hooks';
import { Button, Group, Skeleton, Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { Modal } from '@repletoryreach/sdk-ui/overlays';
import { TextInput } from '@repletoryreach/sdk-ui/inputs';
import { useT } from '../../../i18n/I18nProvider';
import { formatRelative } from '../../../lib/format';
import { StatusTag } from '../components/TicketBits';
import { channelLabel, ticketErrorText } from '../lib/data';

const MUTED = 'var(--rr-color-text-muted)';

/** Merge this ticket into another, found by its number. */
export function MergeModal({ ticket, opened, onClose, onMerged }: {
  ticket: tickets.Ticket;
  opened: boolean;
  onClose: () => void;
  onMerged: (target: tickets.Ticket) => void;
}) {
  const t = useT();
  const [search, setSearch] = useState('');
  const [target, setTarget] = useState<tickets.TicketSummary | null>(null);
  const q = useDebounce(search.trim(), 250);
  useEffect(() => { if (opened) { setSearch(''); setTarget(null); } }, [opened]);

  const results = useQuery({
    queryKey: ['tickets', 'merge-search', q],
    queryFn: () => tickets.listTickets({ q, limit: 10 }),
    enabled: opened && q.length >= 2
  });
  const rows = (results.data?.data ?? []).filter((x) => x.id !== ticket.id && x.status.category !== 'closed');

  const merge = useMutation({
    mutationFn: () => tickets.mergeTicket(ticket.id, target!.id),
    onSuccess: (res) => { onMerged(res.data); onClose(); }
  });

  return (
    <Modal opened={opened} onClose={onClose} title={t('tickets.merge.title', { number: ticket.number })} size="md">
      <Stack gap="md">
        <TextInput
          label={t('tickets.merge.search')}
          value={search}
          onChange={(e) => { setSearch(e.currentTarget.value); setTarget(null); }}
          description={t('tickets.merge.searchHint')}
          data-autofocus
        />
        {results.isFetching && <Skeleton height={60} />}
        {results.error && <Alert color="error" role="alert">{ticketErrorText(t, results.error)}</Alert>}
        {q.length >= 2 && results.isSuccess && rows.length === 0 && <Text role="bodySmall" c={MUTED}>{t('tickets.merge.noMatch')}</Text>}
        <div role="listbox" aria-label={t('tickets.merge.search')} className="rr-tickets__pick-list">
          {rows.map((x) => (
            <button key={x.id} type="button" role="option" aria-selected={target?.id === x.id}
              className={`rr-tickets__pick${target?.id === x.id ? ' is-active' : ''}`} onClick={() => setTarget(x)}>
              <Group gap="sm" justify="space-between" wrap="nowrap">
                <Stack gap={0} style={{ minWidth: 0 }}>
                  <Text role="bodySmall" fw={600}>{x.number} · {x.subject}</Text>
                  <Text role="caption" c={MUTED}>{x.contact?.name || x.contact?.ref || t('tickets.noClient')}</Text>
                </Stack>
                <StatusTag status={x.status} />
              </Group>
            </button>
          ))}
        </div>
        {target && <Alert color="warning">{t('tickets.merge.warning', { from: ticket.number, into: target.number })}</Alert>}
        {merge.error && <Alert color="error" role="alert">{ticketErrorText(t, merge.error)}</Alert>}
        <Group gap="sm" justify="flex-end">
          <Button variant="default" onClick={onClose}>{t('action.cancel')}</Button>
          <Button color="red" disabled={!target} loading={merge.isPending} onClick={() => merge.mutate()}>
            {target ? t('tickets.merge.confirm', { into: target.number }) : t('tickets.merge.action')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Link another conversation (say, the same client by email as well as WhatsApp). */
export function LinkModal({ ticket, opened, onClose, onLinked }: {
  ticket: tickets.Ticket;
  opened: boolean;
  onClose: () => void;
  onLinked: (ticket: tickets.Ticket) => void;
}) {
  const t = useT();
  const [search, setSearch] = useState('');
  const q = useDebounce(search.trim(), 250);
  useEffect(() => { if (opened) setSearch(ticket.contact?.name || ticket.contact?.ref || ''); }, [opened]); // eslint-disable-line react-hooks/exhaustive-deps

  const results = useQuery({
    queryKey: ['tickets', 'link-search', q],
    queryFn: () => inbox.listConversations({ search: q || undefined, limit: 10 }),
    enabled: opened
  });
  const linked = new Set(ticket.conversations.map((c) => c.id));
  const rows = (results.data?.data ?? []).filter((c) => !linked.has(c.id));

  const link = useMutation({
    mutationFn: (conversationId: string) => tickets.linkConversation(ticket.id, conversationId),
    onSuccess: (res) => { onLinked(res.data); onClose(); }
  });

  return (
    <Modal opened={opened} onClose={onClose} title={t('tickets.link.title')} size="md">
      <Stack gap="md">
        <TextInput
          label={t('tickets.link.search')}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          description={t('tickets.link.searchHint')}
          data-autofocus
        />
        {results.isPending && <Skeleton height={60} />}
        {results.error && (
          <Alert color="error" role="alert">
            <Group gap="sm" justify="space-between" wrap="wrap">
              <span>{ticketErrorText(t, results.error)}</span>
              <Button size="xs" variant="default" onClick={() => void results.refetch()}>{t('action.retry')}</Button>
            </Group>
          </Alert>
        )}
        {results.isSuccess && rows.length === 0 && <Text role="bodySmall" c={MUTED}>{t('tickets.link.noMatch')}</Text>}
        <Stack gap="xs">
          {rows.map((c) => (
            <Group key={c.id} gap="sm" justify="space-between" wrap="nowrap" className="rr-tickets__pick">
              <Stack gap={0} style={{ minWidth: 0 }}>
                <Text role="bodySmall" fw={600}>{c.contact_display_name || c.contact_msisdn || c.contact_ref}</Text>
                <Text role="caption" c={MUTED} lineClamp={1}>
                  {channelLabel(t, c.channel)} · {formatRelative(c.last_message_at)}{c.last_message_preview ? ` · ${c.last_message_preview}` : ''}
                </Text>
              </Stack>
              <Button size="xs" variant="default" loading={link.isPending && link.variables === c.id} onClick={() => link.mutate(c.id)}>
                {t('tickets.link.link')}
              </Button>
            </Group>
          ))}
        </Stack>
        {link.error && <Alert color="error" role="alert">{ticketErrorText(t, link.error)}</Alert>}
      </Stack>
    </Modal>
  );
}
